import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './StepFiveCommon.css';
import { useNavigate } from "react-router-dom";
import { documentsAPI } from '../../api/endpoints/documents';
import { PDFDocument } from 'pdf-lib'
import { customDocuments, allDocuments } from '../../utils/documentConfigs';
import { getInitialDocumentStates } from '../../utils/documentHelpers';

const StepFiveCommon = ({ vehiculeId, user_role, onPrevious, commonData }) => {
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5002/api";
    const navigate = useNavigate();
    const [selectedDocuments, setSelectedDocuments] = useState(getInitialDocumentStates());
    const resetSelections = () => { setSelectedDocuments(getInitialDocumentStates())}

    const handleDocumentChange = (documentKey) => {
        setSelectedDocuments({
            ...selectedDocuments,
            [documentKey]: !selectedDocuments[documentKey]
        });
    };

    const handleDownload = async () => {
        const selectedDocKeys = Object.keys(selectedDocuments).filter(key => selectedDocuments[key]);
        if (selectedDocKeys.length === 0) {
            alert("Veuillez sélectionner au moins un document à télécharger.");
            return;
        }

        const cerfaTypes = selectedDocKeys
            .map(key => allDocuments.find(doc => doc.key === key)?.cerfa?.replace("*", "-"))
            .filter(Boolean);

        const token = localStorage.getItem("token");
        const generatedBlobs = [];

        const tryGenerateWithGroup = async (groupIds, filename, token, data) => {
            for (const groupId of groupIds) {
                try {
                    const documentId = await documentsAPI.resolveDocumentId(groupId, filename);
                    const res = await fetch(`${API_URL}/documents/generate`, {
                        method: "POST",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({ groupId, documentId, data }),
                    });
                    if (res.ok) return await res.blob();
                } catch (err) {
                    console.warn(`Document ${filename} introuvable pour le groupe ${groupId}`);
                }
            }
            throw new Error(`Échec de génération du document ${filename}`);
        };

        try {
            // 1. Cerfa PDF
            if (cerfaTypes.length > 0) {
                const res = await fetch(`${API_URL}/pdfs/generate-pdf`, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        selectedVehicles: [parseInt(vehiculeId)],
                        formData: {},
                        cerfa_types: cerfaTypes,
                    }),
                });

                if (!res.ok) throw new Error("Erreur PDF CERFA");
                const cerfaBlob = await res.blob();
                generatedBlobs.push(cerfaBlob);
            }

            // 2. Documents personnalisés
            const documentConfigs = customDocuments(vehiculeId, commonData);

            for (const doc of documentConfigs) {
                if (selectedDocuments[doc.key]) {
                    try {
                        const blob = await tryGenerateWithGroup(
                            doc.groups,
                            doc.filename,
                            token,
                            doc.getData()
                        );
                        generatedBlobs.push(blob);
                    } catch (e) {
                        console.warn(`Erreur lors de la génération du document "${doc.key}":`, e);
                    }
                }
            }

            // 3. Fusion des documents
            const mergedPdf = await PDFDocument.create();

            const mergeBlob = async (blob) => {
                const arrayBuffer = await blob.arrayBuffer();
                const pdf = await PDFDocument.load(arrayBuffer);
                const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
                pages.forEach((page) => mergedPdf.addPage(page));
            };

            for (const blob of generatedBlobs) {
                await mergeBlob(blob);
            }

            const mergedBytes = await mergedPdf.save();
            const mergedBlob = new Blob([mergedBytes], { type: "application/pdf" });
            const mergedUrl = URL.createObjectURL(mergedBlob);

            const a = document.createElement('a');
            a.href = mergedUrl;
            a.download = "Documents_fusionnes.pdf";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            window.open(mergedUrl, "_blank");
            resetSelections(setSelectedDocuments);
        } catch (err) {
            console.error("Erreur dans handleDownload:", err);
            alert(err.message || "Erreur lors du téléchargement.");
        }
    };

    const handleBackToDashboard = () => {
        navigate(`/${user_role}-dashboard`);
    };

    const handlePrevious = () => {
        if (onPrevious) {
            onPrevious();
        } else {
            window.history.back();
        }
    };

    return (
        <div className="step-five-container">
            <div className="congratulations-section">
                <h2 className="success-title">Bravo !</h2>
                <p className="success-message">
                    Bravo, votre nouveau formulaire a bien été créé.
                    <br />
                    Vous pouvez dès à présent sélectionner et télécharger les documents suivants :
                </p>
            </div>

            <div className="documents-section">
                <div className="documents-card">
                    <div className="documents-header">
                        <h3>VOS DOCUMENTS</h3>
                        <div className="document-icon">
                            <i className="fas fa-file-alt"></i>
                        </div>
                    </div>

                    <div className="documents-list">
                        {allDocuments
                            .filter(doc => {
                                if (doc.cerfa) return true;
                                const groupId = commonData?.concession?.group_id || commonData?.concession_groupe_id;
                                return doc.groups?.includes(Number(groupId));
                            })
                            .map(({ key, label, cerfa }) => (
                                <div className="document-item" key={key}>
                                    <input
                                    type="checkbox"
                                    id={key}
                                    name={key}
                                    checked={selectedDocuments[key] || false}
                                    onChange={() => handleDocumentChange(key)}
                                    />
                                    <label htmlFor={key}>
                                    {label}
                                    {cerfa && <span className="cerfa-number">(Cerfa {cerfa})</span>}
                                    <span className="not-available"></span>
                                    </label>
                                </div>
                            ))
                        }
                    </div>
                </div>
            </div>

            <div className="download-section">
                <button className="download-button" onClick={handleDownload}>
                    Télécharger
                </button>
                <p className="download-note">les documents pré-remplis au format PDF</p>
            </div>

            <div className="navigation-buttons">
                <button className="previous-button" onClick={handlePrevious}>
                    PRÉCÉDENT
                </button>
                <button className="dashboard-button" onClick={handleBackToDashboard}>
                    Voir mon tableau de bord
                </button>
            </div>
        </div>
    );
};

StepFiveCommon.propTypes = {
    vehiculeId: PropTypes.number.isRequired,
    user_role: PropTypes.string.isRequired,
    onPrevious: PropTypes.func,
    commonData: PropTypes.object.isRequired
};

export default StepFiveCommon;